import type { MetaFunction } from '@remix-run/node';
import { Link, useNavigate, useSearchParams } from '@remix-run/react';
import { useCallback, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { useGoogleAuth } from '~/hooks';
import { trackConversion } from '~/lib/analytics';
import { post, reset401Flag, syncAuthCookie } from '~/lib/api';
import { useAppStore } from '~/stores';

export const meta: MetaFunction = () => [{ title: 'Register — Deleg8 Studio' }];

interface AuthResponse {
  token: string;
  user: { id: string; email: string; name: string };
  organizations: Array<{ id: string; name: string; role: string }>;
}

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser, setOrganizationId, setOrganizations } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get('plan');
  const defaultRedirect = planKey
    ? `/app/checkout?plan=${planKey}`
    : '/app/checkout';
  const redirectTo = searchParams.get('redirect') || defaultRedirect;
  const loginLink = searchParams.toString()
    ? `/login?${searchParams.toString()}`
    : '/login';

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      if (!acceptedTerms) {
        toast.error('Please accept the Terms of Service and Privacy Policy.');
        return;
      }
      setLoading(true);
      reset401Flag();
      try {
        const res = await post<AuthResponse>('/auth/google', { credential });
        localStorage.setItem('auth_token', res.token);
        syncAuthCookie(res.token);
        setUser({ id: res.user.id, email: res.user.email, name: res.user.name });
        setOrganizations(res.organizations);
        if (res.organizations.length > 0) {
          setOrganizationId(res.organizations[0].id);
        }
        trackConversion('sign_up', { method: 'google' });
        navigate(redirectTo);
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Google sign-up failed';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [
      acceptedTerms,
      navigate,
      redirectTo,
      setOrganizationId,
      setOrganizations,
      setUser,
    ],
  );

  const { prompt: promptGoogle, isAvailable: googleAvailable } =
    useGoogleAuth(handleGoogleCredential);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await post<AuthResponse>('/auth/register', {
        email,
        password,
        fullName,
      });
      localStorage.setItem('auth_token', res.token);
      syncAuthCookie(res.token);
      setUser({ id: res.user.id, email: res.user.email, name: res.user.name });
      setOrganizations(res.organizations);
      if (res.organizations.length > 0) {
        setOrganizationId(res.organizations[0].id);
      }
      trackConversion('sign_up', { method: 'email' });
      navigate(redirectTo);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Registration failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-900 via-creative-950 to-surface-900 px-4">
      {/* Gradient blobs */}
      <div className="pointer-events-none absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full bg-gradient-to-br from-creative-100/40 to-accent-100/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-gradient-to-tl from-heat-100/30 to-creative-100/20 blur-3xl" />

      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-glow p-8"
      >
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-gradient-brand">
          Deleg8 Studio
        </p>
        <h1 className="mt-2 text-2xl font-bold text-white">Create account</h1>
        <p className="mt-1 text-sm text-white/60">
          Get started with Deleg8 Studio
        </p>

        <label className="mt-6 flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-white/80">Full name</span>
          <input
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Sabir Salah"
            className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-3 py-2 text-sm shadow-sm outline-none transition focus:border-creative-400 focus:ring-2 focus:ring-creative-400/30"
          />
        </label>

        <label className="mt-4 flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-white/80">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-3 py-2 text-sm shadow-sm outline-none transition focus:border-creative-400 focus:ring-2 focus:ring-creative-400/30"
          />
        </label>

        <label className="mt-4 flex flex-col gap-1.5">
          <span className="text-sm font-semibold text-white/80">Password</span>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min. 6 characters"
            className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-3 py-2 text-sm shadow-sm outline-none transition focus:border-creative-400 focus:ring-2 focus:ring-creative-400/30"
          />
        </label>

        <label className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-sm text-white/80">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-white/30 bg-transparent text-creative-400 focus:ring-creative-400"
            required
          />
          <span className="leading-relaxed">
            I agree to the{' '}
            <Link to="/terms" className="font-semibold text-creative-300 hover:text-creative-200">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              className="font-semibold text-creative-300 hover:text-creative-200"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={loading || !acceptedTerms}
          className="mt-6 w-full rounded-xl border-0 bg-gradient-to-r from-creative-600 via-accent-500 to-heat-500 px-4 py-2.5 text-sm font-semibold text-white shadow-glow transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Creating account...' : 'Create account'}
        </button>

        {googleAvailable && (
          <>
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/20" />
              <span className="text-xs text-white/60">or</span>
              <div className="h-px flex-1 bg-white/20" />
            </div>

            <button
              type="button"
              disabled={loading || !acceptedTerms}
              onClick={promptGoogle}
              className="mt-4 flex w-full items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Sign up with Google
            </button>
          </>
        )}

        <p className="mt-4 text-center text-sm text-white/60">
          Already have an account?{' '}
          <Link to={loginLink} className="font-semibold text-creative-300 hover:text-creative-200">
            Sign in
          </Link>
        </p>
      </form>
    </main>
  );
}
